import { GTMProvider } from '@elgorditosalsero/react-gtm-hook'
import { HelmetProvider } from 'react-helmet-async'
import { CssBaseline, ThemeProvider } from '@vibialighting/ui-kit'

import Config from '@/config'
import { IntlWrapper as IntlWrapperContent } from '@/contexts/IntlWrapper'
import { AuthProvider } from '@/contexts/AuthContext'
import { ModalProvider } from '@/contexts/ModalContext'
import { WebSocketProvider } from '@/contexts/WebSocketContext'
import { MainRouter, IframeRouter } from '@/routers'
import { Router } from 'react-router-dom'
import { HistoryProvider } from '@/contexts/HistoryContext'

import { Provider as ReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { initializeStore } from '@/redux/store'
import rootSaga from '@/redux/sagas'

import ScrollToTopOnMount from '@/components/ScrollToTopOnMount'
import { BrowserRouter } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import DataLayerWrapper from '@/contexts/DataLayerWrapper'
import RetryActionLayer from '@/components/RetryActionLayer'
import { shallowEqual, useSelector } from 'react-redux'
import * as Sentry from "@sentry/react";

const host = process.env.REACT_APP_PLATFORM_HOST;

const sentryEnvironments = [
	'pablo.vibia.io',
	'vibia.dev',
	'vibiatest.com',
	'vibia.com'
]

if (sentryEnvironments.includes(host)) {
	Sentry.init({
		dsn: 'https://9d0788b19033dce73194c984e6a20364@o4505906214076416.ingest.sentry.io/4506145695072256',
		environment: host,
		dist: host,
		release: process.env.REACT_APP_SENTRY_RELEASE,
		integrations: [
			new Sentry.BrowserTracing({
				// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
				// tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
			}),
			// new Sentry.Replay(),
		],
		// Performance Monitoring
		tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
		// Session Replay
		// replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		// replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	});
}

const gtmParams = { id: 'GTM-K4TQLZL' }

const { store, persistor } = initializeStore()

store.runSaga(rootSaga)

const App = () => {
	const config = Config.getInstance().get()
	const history = createBrowserHistory()
	const isIframe = /^\/iframe\/*/.test(window.location?.pathname)

	if (config === null) {
		return <div>NO CONFIG AVAILABLE</div>
	}

	return isIframe ? (
		<Router history={history}>
			<IframeRouter />
		</Router>
	) : (
		<>
			<ReduxProvider store={store}>
				<PersistGate loading={null} persistor={persistor}>
					<CssBaseline />
					<GTMProvider state={gtmParams}>
						<IntlWrapper>
							<BrowserRouter>
								<ThemeProvider>
									<WebSocketProvider>
										<HistoryProvider history={history}>
											<ModalProvider>
												<AuthProvider>
													<ScrollToTopOnMount />
													<RetryActionLayer />
													<HelmetProvider>
														<Router history={history}>
															<DataLayerWrapper />
															<MainRouter />
														</Router>
													</HelmetProvider>
												</AuthProvider>
											</ModalProvider>
										</HistoryProvider>
									</WebSocketProvider>
								</ThemeProvider>
							</BrowserRouter>
						</IntlWrapper>
					</GTMProvider>
				</PersistGate>
			</ReduxProvider>
		</>
	)
}

const IntlWrapper = ({ children }) => {
	const navigationLanguage = useSelector(state => state.ui.navigation.language, shallowEqual)
	return (
		<IntlWrapperContent language={navigationLanguage}>
			{children}
		</IntlWrapperContent>
	)
}

export default App
